import React, { useState, useMemo } from 'react';
import { WordpressArticle, filterArticles } from '@ais-ui/utils';
import { useArticles } from '@ais-ui/api';
import { useLocalization } from '@ais-ui/locale-management';
import { HelpLayout } from './HelpLayout';
import { HelpSidebar } from './HelpSidebar';
import { HelpHeader } from './HelpHeader';
import { HelpArticleContent } from './HelpArticleContent';
import { Button } from '@ais-ui/ui-core';
import { LoadingSpinner } from '@ais-ui/ui-core';
import { categoryToIcon } from './categoryToIcon';

interface HelpDocumentationProps {
  initialArticleId?: number;
  /** Whether to show a close button (for popup mode) */
  showCloseButton?: boolean;
  /** Called when close button is clicked */
  onClose?: () => void;
}

export const HelpDocumentation: React.FC<HelpDocumentationProps> = ({
  initialArticleId,
  showCloseButton = false,
  onClose,
}) => {
  const { messages } = useLocalization();
  const { data: articles, error, isLoading } = useArticles();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedArticle, setSelectedArticle] =
    useState<WordpressArticle | null>(null);

  // Filter articles based on search
  const filteredArticles = useMemo(() => {
    if (!articles) return [];
    if (!searchQuery.trim()) return articles;
    return filterArticles(articles, searchQuery);
  }, [articles, searchQuery]);

  // Set initial article or first article when data loads
  React.useEffect(() => {
    if (articles && articles.length > 0 && !selectedArticle) {
      if (initialArticleId) {
        const initialArticle = articles.find(
          (a: WordpressArticle) => a.id === initialArticleId
        );
        setSelectedArticle(initialArticle || articles[0]);
      } else {
        setSelectedArticle(articles[0]);
      }
    }
  }, [articles, initialArticleId, selectedArticle]);

  const handleArticleSelect = (article: WordpressArticle) => {
    setSelectedArticle(article);
    setSearchQuery(''); // Clear search when selecting an article
  };

  const handleBackToOverview = () => {
    setSelectedArticle(null);
    setSearchQuery('');
  };

  // Loading state
  if (isLoading) {
    return (
      <div className="h-screen flex items-center justify-center">
        <div className="text-center">
          <LoadingSpinner size="big" className="mb-4" />
          <p className="text-muted-foreground">Loading help articles...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="h-screen flex items-center justify-center">
        <div className="text-center max-w-md">
          <h2 className="text-xl font-semibold mb-2">Unable to load help</h2>
          <p className="text-muted-foreground mb-4">
            We couldn't load the help articles. Please try again later.
          </p>
          <Button onClick={() => window.location.reload()} variant="outline">
            Retry
          </Button>
        </div>
      </div>
    );
  }

  // No articles state
  if (!articles || articles.length === 0) {
    return (
      <div className="h-screen flex items-center justify-center">
        <div className="text-center max-w-md">
          <h2 className="text-xl font-semibold mb-2">
            No help articles available
          </h2>
          <p className="text-muted-foreground">
            Help content is not available at the moment.
          </p>
        </div>
      </div>
    );
  }

  return (
    <HelpLayout
      header={
        <HelpHeader
          searchQuery={searchQuery}
          onSearchChange={setSearchQuery}
          showCloseButton={showCloseButton}
          onClose={onClose}
        />
      }
      sidebar={
        <HelpSidebar
          articles={filteredArticles}
          activeArticleId={selectedArticle?.id}
          onArticleSelect={handleArticleSelect}
          searchQuery={searchQuery}
        />
      }
    >
      {selectedArticle ? (
        <HelpArticleContent article={selectedArticle} />
      ) : (
        // Overview/Welcome page when no article is selected
        <div className="prose prose-slate dark:prose-invert max-w-none">
          <h1>{messages.helpTitle || 'Help & Documentation'}</h1>
          <p className="lead">
            {messages.helpSubtitle ||
              'Find answers and learn how to use the application effectively.'}
          </p>

          <div className="not-prose mt-8">
            <h2 className="text-lg font-semibold mb-4">Browse by Category</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Generate category overview cards */}
              {Array.from(
                new Set(
                  articles
                    .map((a: WordpressArticle) => a.categories?.[0])
                    .filter(Boolean)
                )
              ).map((category) => {
                const categoryString = category as string;
                const categoryArticles = articles.filter(
                  (a: WordpressArticle) => a.categories?.[0] === categoryString
                );
                const CategoryIcon = categoryToIcon([categoryString]);

                return (
                  <div
                    key={categoryString}
                    className="border rounded-lg p-4 hover:bg-accent/50 transition-colors"
                  >
                    <div className="flex items-center mb-2">
                      <CategoryIcon className="h-5 w-5 mr-2" />
                      <h3 className="font-medium capitalize">
                        {categoryString}
                      </h3>
                    </div>
                    <p className="text-sm text-muted-foreground mb-3">
                      {categoryArticles.length} article
                      {categoryArticles.length !== 1 ? 's' : ''}
                    </p>
                    <ul className="text-sm space-y-1">
                      {categoryArticles
                        .slice(0, 3)
                        .map((article: WordpressArticle) => (
                          <li key={article.id}>
                            <Button
                              variant="link"
                              className="h-auto p-0 text-left justify-start"
                              onClick={() => handleArticleSelect(article)}
                            >
                              {article.title.rendered}
                            </Button>
                          </li>
                        ))}
                      {categoryArticles.length > 3 && (
                        <li className="text-muted-foreground">
                          +{categoryArticles.length - 3} more...
                        </li>
                      )}
                    </ul>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </HelpLayout>
  );
};
